import { pwaConfig } from "./pwa-config";
import { serviceWorkerScriptURL } from "./service-worker-source";

interface WorkerState {
  readonly scriptURL: string;
}

export interface SnakelyServiceWorkerRegistration {
  readonly active: WorkerState | null;
  readonly installing: WorkerState | null;
  readonly waiting: WorkerState | null;
  unregister(): Promise<boolean>;
}

export interface SnakelyServiceWorkerContainer {
  getRegistrations(): Promise<readonly SnakelyServiceWorkerRegistration[]>;
  register(
    scriptURL: string,
    options: { readonly scope: "/"; readonly updateViaCache: "none" },
  ): Promise<SnakelyServiceWorkerRegistration>;
}

export interface PwaRegistrationStart {
  readonly container: SnakelyServiceWorkerContainer | undefined;
  readonly native: boolean;
  readonly origin: string;
  readonly productionStyle: boolean;
  readonly version: string;
}

export type PwaRegistrationResult =
  | { readonly status: "cleaned"; readonly removed: number }
  | { readonly status: "failed" }
  | { readonly status: "native-disabled" }
  | { readonly status: "registered" }
  | { readonly status: "unsupported" };

const isSnakelyRegistration = (
  registration: SnakelyServiceWorkerRegistration,
  origin: string,
): boolean =>
  [registration.active, registration.installing, registration.waiting].some(
    (worker) => {
      if (worker === null) {
        return false;
      }
      const url = new URL(worker.scriptURL, origin);
      return (
        url.origin === origin && url.pathname === pwaConfig.serviceWorkerPath
      );
    },
  );

const startRegistration = async (
  start: PwaRegistrationStart,
): Promise<PwaRegistrationResult> => {
  if (start.native) {
    return { status: "native-disabled" };
  }
  if (start.container === undefined) {
    return { status: "unsupported" };
  }
  try {
    if (start.productionStyle) {
      await start.container.register(serviceWorkerScriptURL(start.version), {
        scope: "/",
        updateViaCache: "none",
      });
      return { status: "registered" };
    }
    const registrations = await start.container.getRegistrations();
    const owned = registrations.filter((registration) =>
      isSnakelyRegistration(registration, start.origin),
    );
    const results = await Promise.all(
      owned.map((registration) => registration.unregister()),
    );
    return {
      removed: results.filter((removed) => removed).length,
      status: "cleaned",
    };
  } catch {
    return { status: "failed" };
  }
};

export const serviceWorkerContainerFor = (
  navigatorScope: { readonly serviceWorker: SnakelyServiceWorkerContainer },
  native: boolean,
): SnakelyServiceWorkerContainer | undefined =>
  native ? undefined : navigatorScope.serviceWorker;

export class PwaRegistrationCoordinator {
  private result: Promise<PwaRegistrationResult> | undefined;

  public start(start: PwaRegistrationStart): Promise<PwaRegistrationResult> {
    this.result ??= startRegistration(start);
    return this.result;
  }
}
